import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ArrowDownToLine,
  ArrowUpToLine,
  Copy,
  EyeOff,
  Keyboard,
  Lock,
  Maximize,
  Minus,
  Plus,
  RotateCw,
  Trash2,
  Unlock,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { backgroundImageStyle, layersInOrder, PAGE_HEIGHT, PAGE_WIDTH, textFieldStyle, withElementDefaults } from '../../utils/invitationStyle';
import { BackgroundTint, ElementView, TextContent } from '../design/DesignLayers';
import { displayText } from '../../utils/affix';
import { pageToFlat, perspectiveForBackground, perspectiveSurfaceStyle } from '../../utils/perspective';
import { FOCUS_RING } from './constants';
import { canMoveLayer, clamp, findItem, isDeletable, itemLabel, round, updateItem } from './designUtils';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;
const SNAP = 0.8; // % distance that snaps to the page centre lines
const MIN_BOX = 8; // base px

const rad = (deg) => (deg * Math.PI) / 180;
const rotate = ([x, y], deg) => {
  const r = rad(deg);
  return [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
};

/** Size of the stage the page is fitted into. */
const useStage = () => {
  const [node, setNode] = useState(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const ref = useCallback((el) => setNode(el), []);
  useEffect(() => {
    if (!node) return undefined;
    const update = () => {
      const r = node.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return [ref, size, node];
};

// (Templates' old "watermark_image" files are whole old designs with text: never laid over the canvas.)
const Watermark = ({ scale }) => (
  <div className="absolute inset-0 pointer-events-none z-[5] flex items-center justify-center overflow-hidden" aria-hidden="true">
    <div
      className="font-sans font-extrabold -rotate-[30deg] select-none"
      style={{ fontSize: 110 * scale, color: 'rgba(26,26,26,0.08)' }}
    >
      PREVIEW
    </div>
  </div>
);

const barBtn = cn(
  'h-9 px-3 rounded-full whitespace-nowrap text-[13px] font-bold text-ink flex items-center gap-1.5 hover:bg-cream disabled:opacity-40 disabled:hover:bg-transparent',
  FOCUS_RING
);

/** Floating bar with actions for the selected item. */
const SelectionBar = ({ design, selectedId, compact, showLabel, actions }) => {
  const found = findItem(design, selectedId);
  if (!found) return null;
  const { item } = found;
  const deletable = isDeletable(found);
  const buttons = [
    ['Duplicate', Copy, actions.duplicate, false],
    ['Bring forward', ArrowUpToLine, () => actions.move(1), !canMoveLayer(design, selectedId, 1)],
    ['Send back', ArrowDownToLine, () => actions.move(-1), !canMoveLayer(design, selectedId, -1)],
    [item.locked ? 'Unlock' : 'Lock', item.locked ? Unlock : Lock, actions.toggleLock, false],
  ];
  return (
    <div
      role="toolbar"
      aria-label={`Actions for ${itemLabel(found)}`}
      className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 p-1 rounded-full gradient-border shadow-[0_12px_30px_-18px_rgba(26,26,26,0.35)] max-w-[calc(100%-24px)]"
      data-testid="selection-bar"
    >
      {showLabel && (
        <>
          <span className="h-9 px-3 flex items-center text-[13px] font-extrabold text-ink truncate max-w-[160px]">
            {itemLabel(found)}
          </span>
          <span className="w-px h-5 bg-[#E6E1D6]" aria-hidden="true" />
        </>
      )}
      {buttons.map(([label, Icon, onClick, disabled]) => (
        <button
          key={label}
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          title={label}
          className={cn(barBtn, compact && 'w-9 px-0 justify-center')}
          data-testid={`sel-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
          {compact ? <Icon className="h-4 w-4" aria-hidden="true" /> : label}
        </button>
      ))}
      <button
        type="button"
        onClick={actions.remove}
        aria-label={deletable ? 'Delete' : 'Hide this line'}
        title={deletable ? 'Delete' : 'Template lines can be hidden, not deleted'}
        className={cn(barBtn, 'text-[#B0003A]', compact && 'w-9 px-0 justify-center')}
        data-testid="sel-delete"
      >
        {compact ? (
          deletable ? <Trash2 className="h-4 w-4" aria-hidden="true" /> : <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : deletable ? (
          'Delete'
        ) : (
          'Hide'
        )}
      </button>
    </div>
  );
};

const ALIGN_BUTTONS = [
  ['left', 'Align left edges', AlignStartVertical],
  ['hcenter', 'Align centres (left-right)', AlignCenterVertical],
  ['right', 'Align right edges', AlignEndVertical],
  ['top', 'Align top edges', AlignStartHorizontal],
  ['vcenter', 'Align middles (up-down)', AlignCenterHorizontal],
  ['bottom', 'Align bottom edges', AlignEndHorizontal],
];

const iconBtn = cn('h-9 w-9 rounded-full flex items-center justify-center text-ink hover:bg-cream disabled:opacity-40 disabled:hover:bg-transparent', FOCUS_RING);

/**
 * Align / distribute buttons. One item: aligns it on the page. Several: aligns them to each other
 * (InDesign-style); distribute needs three or more.
 */
const ArrangeBar = ({ count, onAlign, onDistribute, onShortcuts, extra = null, label }) => (
  <div
    role="toolbar"
    aria-label={count > 1 ? 'Align the selected items' : 'Align on the page'}
    className="flex items-center gap-0.5 p-1 rounded-full gradient-border shadow-[0_12px_30px_-18px_rgba(26,26,26,0.35)] max-w-[calc(100vw-24px)] overflow-x-auto"
    data-testid="arrange-bar"
  >
    {label ? <span className="h-9 px-3 flex items-center text-[13px] font-extrabold text-ink whitespace-nowrap">{label}</span> : null}
    {ALIGN_BUTTONS.map(([mode, title, Icon]) => (
      <button
        key={mode}
        type="button"
        onClick={() => onAlign(mode)}
        aria-label={count > 1 ? title : `${title.replace(/ edges| \(.*\)|s$/g, '')} on the page`}
        title={count > 1 ? title : `${title} — on the page`}
        className={iconBtn}
        data-testid={`align-${mode}`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    ))}
    {count > 2 ? (
      <>
        <span className="w-px h-5 bg-[#E6E1D6] mx-0.5" aria-hidden="true" />
        <button type="button" onClick={() => onDistribute('h')} aria-label="Spread evenly left to right" title="Spread evenly left to right" className={iconBtn} data-testid="distribute-h">
          <AlignHorizontalDistributeCenter className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" onClick={() => onDistribute('v')} aria-label="Spread evenly top to bottom" title="Spread evenly top to bottom" className={iconBtn} data-testid="distribute-v">
          <AlignVerticalDistributeCenter className="h-4 w-4" aria-hidden="true" />
        </button>
      </>
    ) : null}
    {extra}
    {onShortcuts ? (
      <>
        <span className="w-px h-5 bg-[#E6E1D6] mx-0.5" aria-hidden="true" />
        <button type="button" onClick={onShortcuts} aria-label="Keyboard shortcuts" title="Keyboard shortcuts (?)" className={iconBtn}>
          <Keyboard className="h-4 w-4" aria-hidden="true" />
        </button>
      </>
    ) : null}
  </div>
);

/** Holds the design layers: as-is on a flat template, warped onto the card on a side-view one. */
const DesignSurface = ({ perspective, width, height, children }) => {
  const style = perspectiveSurfaceStyle(perspective, width, height);
  if (!style) return children;
  return (
    <div data-surface="1" style={style} data-testid="perspective-surface">
      {children}
    </div>
  );
};

const HANDLE = 'absolute h-[11px] w-[11px] rounded-full bg-white border-[1.5px] touch-none';

/**
 * The editable page: fitted to its stage, with selection, drag to move,
 * corner handles to resize, a rotate handle, snapping guides and zoom.
 */
export const StudioCanvas = ({
  design,
  selectedId,
  selection,
  onSelectionChange,
  onSelect,
  onTextEdit, // (id, text) — in-place editing (double-click a text box)
  onCaret, // (id, start, end) — selection inside the box being edited
  caret, // { id, start, end, tick } — restore the selection after a glyph is inserted
  onChange,
  onGestureEnd,
  backgroundImage,
  backgroundTint,
  perspective: templatePerspective, // side-view template: the design layer is warped onto these corners (utils/perspective.js)
  showWatermark,
  interactive = true,
  compact = false,
  actions,
  footerNote,
}) => {
  const [stageRef, stage, stageNode] = useStage();
  const perspective = useMemo(() => perspectiveForBackground(templatePerspective, backgroundTint), [templatePerspective, backgroundTint]);
  const pageRef = useRef(null);
  const gesture = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [guides, setGuides] = useState({ v: [], h: [] }); // snap lines, in % of the page
  const [box, setBox] = useState(null);
  const [marquee, setMarquee] = useState(null); // {x0,y0,x1,y1} in % while drag-selecting
  const [rects, setRects] = useState({}); // id -> {l,t,r,b} in % (multi-selection outlines)
  const [editingId, setEditingId] = useState(null); // text box being edited in place
  const [editBox, setEditBox] = useState(null); // its on-screen size (px)
  const editRef = useRef(null);
  const sel = selection || (selectedId ? [selectedId] : []);
  const multi = sel.length > 1;
  const setSel = (ids) => (onSelectionChange ? onSelectionChange(ids) : onSelect(ids[ids.length - 1] || null));

  const pad = compact ? 24 : 56;
  const fit = stage.width ? Math.max(0.05, Math.min((stage.width - pad) / PAGE_WIDTH, (stage.height - pad) / PAGE_HEIGHT)) : 0;
  const scale = fit * zoom;
  const pageW = PAGE_WIDTH * scale;
  const pageH = PAGE_HEIGHT * scale;

  const selected = findItem(design, selectedId);

  // Measure the selected item's unrotated box so the overlay can follow it.
  useLayoutEffect(() => {
    if (!selectedId || !findItem(design, selectedId) || !pageRef.current) {
      setBox(null);
      return undefined;
    }
    const node = pageRef.current.querySelector(`[data-item-id="${CSS.escape(selectedId)}"]`);
    if (!node) {
      setBox(null);
      return undefined;
    }
    const measure = () => {
      const next = { w: node.offsetWidth, h: node.offsetHeight };
      setBox((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [selectedId, design, scale]);

  // Re-measure once web fonts finish loading.
  useEffect(() => {
    if (!document.fonts?.ready) return;
    document.fonts.ready.then(() => setBox((b) => (b ? { ...b } : b)));
  }, []);

  // Pointer -> % of the (flat) design page. On a side-view template the screen point is first
  // mapped back through the perspective, so dragging follows the tilted card.
  const toPct = (e) => {
    const r = pageRef.current.getBoundingClientRect();
    return pageToFlat(perspective, ((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  const toBase = (e) => {
    const [x, y] = toPct(e);
    return [(x / 100) * PAGE_WIDTH, (y / 100) * PAGE_HEIGHT];
  };

  /** On-screen box of each item in % of the page ({l,t,r,b}); rotated items use their bounding box. */
  const measure = useCallback((ids) => {
    const out = {};
    const page = pageRef.current;
    if (!page) return out;
    const pr = page.getBoundingClientRect();
    for (const id of ids) {
      const node = page.querySelector(`[data-item-id="${CSS.escape(id)}"]`);
      if (!node) continue;
      const r = node.getBoundingClientRect();
      const l = ((r.left - pr.left) / pr.width) * 100;
      const rr = ((r.right - pr.left) / pr.width) * 100;
      const t = ((r.top - pr.top) / pr.height) * 100;
      const b = ((r.bottom - pr.top) / pr.height) * 100;
      if (!perspective) {
        out[id] = { l, r: rr, t, b };
        continue;
      }
      // side view: the box's on-screen corners back on the flat page
      const pts = [[l, t], [rr, t], [rr, b], [l, b]].map(([x, y]) => pageToFlat(perspective, x, y));
      out[id] = {
        l: Math.min(...pts.map((q) => q[0])),
        r: Math.max(...pts.map((q) => q[0])),
        t: Math.min(...pts.map((q) => q[1])),
        b: Math.max(...pts.map((q) => q[1])),
      };
    }
    return out;
  }, [perspective]);

  // In-place editing: leave it when the box is deselected or disappears.
  const editingItem = editingId ? findItem(design, editingId)?.item : null;
  useEffect(() => {
    if (editingId && (!editingItem || selectedId !== editingId)) setEditingId(null);
  }, [editingId, editingItem, selectedId]);
  // Size the editor to the (hidden) rendered text, so it wraps exactly like the card.
  useLayoutEffect(() => {
    if (!editingId || !pageRef.current) {
      setEditBox(null);
      return;
    }
    const node = pageRef.current.querySelector(`[data-item-id="${CSS.escape(editingId)}"]`);
    if (!node) return;
    const next = { w: node.offsetWidth, h: node.offsetHeight };
    setEditBox((prev) => (prev && prev.w === next.w && prev.h === next.h ? prev : next));
  });
  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus({ preventScroll: true });
      editRef.current.select();
    }
  }, [editingId]);
  // After a glyph is inserted, put the cursor back after it.
  useEffect(() => {
    const el = editRef.current;
    if (!el || !caret || caret.id !== editingId || !caret.tick) return;
    try {
      el.focus({ preventScroll: true });
      el.setSelectionRange(caret.start, caret.end);
    } catch (e) {
      // ignore
    }
  }, [caret, editingId]);

  // Outlines for every selected item while several are selected.
  const selKey = sel.join(',');
  useLayoutEffect(() => {
    if (!multi) {
      setRects((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }
    setRects(measure(selKey.split(',')));
  }, [multi, selKey, design, scale, measure]);

  /** Align: one item -> on the page; several -> to the selection's own edges / centres. */
  const alignItems = (mode) => {
    const ids = sel.filter((id) => {
      const f = findItem(design, id);
      return f && !f.item.locked && !f.item.hidden;
    });
    if (!ids.length) return;
    const m = measure(ids);
    const all = Object.values(m);
    if (!all.length) return;
    const one = ids.length === 1;
    const bounds = one
      ? { l: 0, t: 0, r: 100, b: 100 }
      : { l: Math.min(...all.map((q) => q.l)), r: Math.max(...all.map((q) => q.r)), t: Math.min(...all.map((q) => q.t)), b: Math.max(...all.map((q) => q.b)) };
    const margin = one ? 5 : 0; // keep a little air from the page edge
    onChange((d) =>
      ids.reduce((acc, id) => {
        const q = m[id];
        const found = findItem(acc, id);
        if (!q || !found) return acc;
        const { x = 50, y = 50 } = found.item;
        const patch = {};
        if (mode === 'left') patch.x = x + (bounds.l + margin - q.l);
        if (mode === 'right') patch.x = x + (bounds.r - margin - q.r);
        if (mode === 'hcenter') patch.x = x + ((bounds.l + bounds.r) / 2 - (q.l + q.r) / 2);
        if (mode === 'top') patch.y = y + (bounds.t + margin - q.t);
        if (mode === 'bottom') patch.y = y + (bounds.b - margin - q.b);
        if (mode === 'vcenter') patch.y = y + ((bounds.t + bounds.b) / 2 - (q.t + q.b) / 2);
        if (patch.x !== undefined) patch.x = round(clamp(patch.x, 0, 100));
        if (patch.y !== undefined) patch.y = round(clamp(patch.y, 0, 100));
        return updateItem(acc, id, patch);
      }, d)
    );
    onGestureEnd?.();
  };

  /** Distribute: equal gaps between three or more items, first and last stay put. */
  const distributeItems = (axis) => {
    const ids = sel.filter((id) => {
      const f = findItem(design, id);
      return f && !f.item.locked && !f.item.hidden;
    });
    if (ids.length < 3) return;
    const m = measure(ids);
    const [lo, hi, key] = axis === 'h' ? ['l', 'r', 'x'] : ['t', 'b', 'y'];
    const order = ids.filter((id) => m[id]).sort((a, b) => m[a][lo] + m[a][hi] - (m[b][lo] + m[b][hi]));
    const first = m[order[0]];
    const last = m[order[order.length - 1]];
    const sizes = order.reduce((s, id) => s + (m[id][hi] - m[id][lo]), 0);
    const gap = (last[hi] - first[lo] - sizes) / (order.length - 1);
    let cursor = first[lo];
    const targets = {};
    for (const id of order) {
      targets[id] = cursor - m[id][lo];
      cursor += m[id][hi] - m[id][lo] + gap;
    }
    onChange((d) =>
      order.reduce((acc, id) => {
        const found = findItem(acc, id);
        if (!found) return acc;
        return updateItem(acc, id, { [key]: round(clamp((found.item[key] ?? 50) + targets[id], 0, 100)) });
      }, d)
    );
    onGestureEnd?.();
  };

  const startGesture = (e, data) => {
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (err) {
      // pointer capture is best effort (not available for synthetic events)
    }
    const startPct = pageRef.current ? toPct(e) : [0, 0];
    gesture.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, startPct, moved: false, key: `${data.type}-${Date.now()}`, ...data };
  };

  const onItemPointerDown = (e, id, item) => {
    if (e.button !== undefined && e.button !== 0) return;
    // Shift / Ctrl / Cmd + click: add to or remove from the selection
    if (interactive && (e.shiftKey || e.ctrlKey || e.metaKey)) {
      e.stopPropagation();
      e.preventDefault();
      setSel(sel.includes(id) ? sel.filter((s) => s !== id) : [...sel, id]);
      return;
    }
    // Pressing on an item that is part of a multi-selection drags the whole group
    if (interactive && multi && sel.includes(id)) {
      e.preventDefault();
      const items = sel
        .map((sid) => findItem(design, sid))
        .filter((f) => f && !f.item.locked)
        .map(({ item: it }) => ({ id: it.id, x: it.x ?? 50, y: it.y ?? 50 }));
      startGesture(e, { type: 'move-group', items });
      return;
    }
    onSelect(id);
    e.currentTarget.focus({ preventScroll: true });
    if (!interactive || item.locked) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    // snap targets: page centre + centres of the other visible items
    const others = layersInOrder(design.text_fields, design.elements)
      .map(({ item: it }) => it)
      .filter((it) => it.id !== id && !it.hidden);
    startGesture(e, {
      type: 'move',
      id,
      origX: item.x ?? 50,
      origY: item.y ?? 50,
      snapX: [50, ...others.map((it) => it.x ?? 50)],
      snapY: [50, ...others.map((it) => it.y ?? 50)],
    });
  };

  /** Drag on an empty part of the page: draw a box and select what it touches. */
  const onBackgroundPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (!interactive || !pageRef.current) {
      onSelect(null);
      return;
    }
    const [x, y] = toPct(e);
    startGesture(e, { type: 'marquee', x0: x, y0: y, additive: e.shiftKey || e.ctrlKey || e.metaKey, base: sel });
  };

  const onHandlePointerDown = (e, corner) => {
    if (!selected || selected.item.locked) return;
    e.preventDefault();
    const { kind, item } = selected;
    const el = kind === 'element' ? withElementDefaults(item) : item;
    const cx = ((el.x ?? 50) / 100) * PAGE_WIDTH;
    const cy = ((el.y ?? 50) / 100) * PAGE_HEIGHT;
    const rot = el.rotation || 0;
    if (corner === 'rotate') {
      startGesture(e, { type: 'rotate', id: item.id, cx, cy });
      return;
    }
    const [sx, sy] = corner;
    if (kind === 'text' && !(e.shiftKey && sy !== 0)) {
      // dragging the frame changes the box width (text wraps), never the font size
      startGesture(e, { type: 'width-text', id: item.id, cx, cy, rot });
      return;
    }
    if (kind === 'text') {
      const [px, py] = toBase(e);
      startGesture(e, {
        type: 'scale-text',
        id: item.id,
        cx,
        cy,
        origSize: item.font_size || 24,
        origDist: Math.max(4, Math.hypot(px - cx, py - cy)),
      });
      return;
    }
    const w = (el.w / 100) * PAGE_WIDTH;
    const h = el.type === 'line' ? 0 : (el.h / 100) * PAGE_HEIGHT;
    const [fx, fy] = rotate([(-sx * w) / 2, (-sy * h) / 2], rot);
    startGesture(e, {
      type: 'resize',
      id: item.id,
      line: el.type === 'line',
      keepRatio: el.type === 'image',
      sx,
      sy,
      rot,
      w,
      h,
      fixed: [cx + fx, cy + fy],
    });
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId || !pageRef.current) return;
    // item, page and stage all listen; handle each event once
    if (e.nativeEvent) {
      if (e.nativeEvent.__studioHandled) return;
      e.nativeEvent.__studioHandled = true;
    }
    if (!g.moved && Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < 3) return;
    g.moved = true;
    const opts = { key: g.key };

    if (g.type === 'marquee') {
      const [x, y] = toPct(e);
      setMarquee({ x0: Math.min(g.x0, x), x1: Math.max(g.x0, x), y0: Math.min(g.y0, y), y1: Math.max(g.y0, y) });
      return;
    }

    if (g.type === 'move-group') {
      const [px, py] = toPct(e);
      const dx = px - g.startPct[0];
      const dy = py - g.startPct[1];
      onChange(
        (d) => g.items.reduce((acc, it) => updateItem(acc, it.id, { x: round(clamp(it.x + dx, 0, 100)), y: round(clamp(it.y + dy, 0, 100)) }), d),
        opts
      );
      return;
    }

    if (g.type === 'move') {
      const [px, py] = toPct(e);
      let x = clamp(g.origX + px - g.startPct[0], 0, 100);
      let y = clamp(g.origY + py - g.startPct[1], 0, 100);
      // snap to the page centre and to other items' centres (hold Alt to move freely)
      const near = (val, list) => (e.altKey ? null : list.reduce((best, t) => (Math.abs(val - t) < SNAP && (best === null || Math.abs(val - t) < Math.abs(val - best)) ? t : best), null));
      const sx = near(x, g.snapX || [50]);
      const sy = near(y, g.snapY || [50]);
      if (sx !== null) x = sx;
      if (sy !== null) y = sy;
      const next = { v: sx === null ? [] : [sx], h: sy === null ? [] : [sy] };
      setGuides((prev) => (prev.v[0] === next.v[0] && prev.h[0] === next.h[0] ? prev : next));
      onChange((d) => updateItem(d, g.id, { x: round(x), y: round(y) }), opts);
      return;
    }

    const [px, py] = toBase(e);
    if (g.type === 'rotate') {
      let deg = (Math.atan2(py - g.cy, px - g.cx) * 180) / Math.PI - 90;
      if (deg < -180) deg += 360;
      if (deg > 180) deg -= 360;
      if (e.shiftKey) deg = Math.round(deg / 15) * 15;
      else {
        const nearest = Math.round(deg / 90) * 90;
        if (Math.abs(deg - nearest) < 4) deg = nearest;
      }
      if (deg === -180) deg = 180;
      onChange((d) => updateItem(d, g.id, { rotation: Math.round(deg) }), opts);
      return;
    }

    if (g.type === 'width-text') {
      const [lx] = rotate([px - g.cx, py - g.cy], -g.rot);
      const width = clamp(round(((2 * Math.abs(lx)) / PAGE_WIDTH) * 100, 1), 3, 100);
      onChange((d) => updateItem(d, g.id, { line_width: width }), opts);
      return;
    }

    if (g.type === 'scale-text') {
      const ratio = Math.hypot(px - g.cx, py - g.cy) / g.origDist;
      const size = clamp(Math.round(g.origSize * ratio), 4, 400);
      onChange((d) => updateItem(d, g.id, { font_size: size }), opts);
      return;
    }

    if (g.type === 'resize') {
      const [lx, ly] = rotate([px - g.fixed[0], py - g.fixed[1]], -g.rot);
      let w = Math.max(MIN_BOX, g.sx * lx);
      let h = g.line ? 0 : Math.max(MIN_BOX, g.sy * ly);
      if (!g.line && (g.keepRatio || e.shiftKey) && g.w && g.h) {
        const k = Math.max(w / g.w, h / g.h);
        w = g.w * k;
        h = g.h * k;
      }
      const [ox, oy] = rotate([(g.sx * w) / 2, (g.sy * h) / 2], g.rot);
      const cx = g.fixed[0] + ox;
      const cy = g.fixed[1] + oy;
      const patch = {
        x: round((cx / PAGE_WIDTH) * 100),
        y: round((cy / PAGE_HEIGHT) * 100),
        w: round(clamp((w / PAGE_WIDTH) * 100, 0.5, 200)),
      };
      if (!g.line) patch.h = round(clamp((h / PAGE_HEIGHT) * 100, 0.5, 200));
      onChange((d) => updateItem(d, g.id, patch), opts);
    }
  };

  const endGesture = (e) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    gesture.current = null;
    setGuides({ v: [], h: [] });
    if (g.type === 'marquee') {
      setMarquee(null);
      if (!g.moved) {
        if (!g.additive) onSelect(null);
        return;
      }
      const [x, y] = toPct(e);
      const box2 = { l: Math.min(g.x0, x), r: Math.max(g.x0, x), t: Math.min(g.y0, y), b: Math.max(g.y0, y) };
      const ids = layersInOrder(design.text_fields, design.elements)
        .map(({ item }) => item)
        .filter((it) => !it.hidden && !it.locked)
        .map((it) => it.id);
      const m = measure(ids);
      const hit = ids.filter((id) => m[id] && m[id].l < box2.r && m[id].r > box2.l && m[id].t < box2.b && m[id].b > box2.t);
      setSel(g.additive ? [...new Set([...g.base, ...hit])] : hit);
      return;
    }
    if (g.moved) onGestureEnd?.();
  };

  const gestureHandlers = {
    onPointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
  };

  const zoomBy = (factor) => setZoom((z) => clamp(round(z * factor, 3), ZOOM_MIN / Math.max(fit, 0.01), ZOOM_MAX / Math.max(fit, 0.01)));

  // Ctrl/Cmd + wheel or Alt + wheel zooms the page.
  useEffect(() => {
    if (!stageNode) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey && !e.altKey) return;
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 1.1 : 1 / 1.1);
    };
    stageNode.addEventListener('wheel', onWheel, { passive: false });
    return () => stageNode.removeEventListener('wheel', onWheel);
  });

  const layers = layersInOrder(design.text_fields, design.elements);
  const selItem = selected ? (selected.kind === 'element' ? withElementDefaults(selected.item) : selected.item) : null;
  const isLine = selected?.kind === 'element' && selItem.type === 'line';
  const showHandles = interactive && selected && !selItem.hidden && !selItem.locked;
  const overlayPad = 6;

  const isText = selected?.kind === 'text';
  const corners = isLine
    ? [[-1, 0], [1, 0]]
    : isText
      ? [[-1, -1], [1, -1], [-1, 1], [1, 1], [-1, 0], [1, 0]] // side handles: box width
      : [[-1, -1], [1, -1], [-1, 1], [1, 1]];

  return (
    <div className="relative flex-1 min-h-0 min-w-0 flex flex-col">
      {interactive && selected && !multi && actions ? (
        <SelectionBar design={design} selectedId={selectedId} compact={compact || stage.width < 760} showLabel={!compact} actions={actions} />
      ) : null}
      {interactive && !compact && sel.length > 0 ? (
        <div className={cn('absolute left-1/2 -translate-x-1/2 z-20', multi ? 'top-3' : 'top-[60px]')}>
          <ArrangeBar
            count={sel.length}
            label={multi ? `${sel.length} selected` : null}
            onAlign={alignItems}
            onDistribute={distributeItems}
            onShortcuts={actions?.shortcuts}
            extra={
              multi && actions ? (
                <>
                  <span className="w-px h-5 bg-[#E6E1D6] mx-0.5" aria-hidden="true" />
                  <button type="button" onClick={actions.duplicateMany} aria-label="Duplicate selected" title="Duplicate (Ctrl+D)" className={iconBtn} data-testid="multi-duplicate">
                    <Copy className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={actions.lockMany} aria-label="Lock or unlock selected" title="Lock / unlock (Ctrl+L)" className={iconBtn} data-testid="multi-lock">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={actions.removeMany} aria-label="Delete selected" title="Delete (template lines are hidden)" className={cn(iconBtn, 'text-[#B0003A]')} data-testid="multi-delete">
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </>
              ) : null
            }
          />
        </div>
      ) : null}

      <div
        ref={stageRef}
        className="flex-1 min-h-0 overflow-auto flex"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) onBackgroundPointerDown(e);
        }}
        {...gestureHandlers}
        data-testid="canvas-stage"
      >
        {scale > 0 && (
          <div
            ref={pageRef}
            className="relative m-auto shrink-0 bg-white shadow-[0_30px_60px_-24px_rgba(26,26,26,0.45)] overflow-hidden select-none"
            style={{ width: pageW, height: pageH, touchAction: 'pan-x pan-y', isolation: 'isolate' }}
            onPointerDown={(e) => {
              if (e.target === e.currentTarget || e.target.dataset.bg || e.target.dataset.tint || e.target.dataset.surface) onBackgroundPointerDown(e);
            }}
            {...gestureHandlers}
            data-testid="invitation-canvas"
          >
            {backgroundImage ? (
              <img
                src={backgroundImage}
                alt=""
                data-bg="1"
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
                style={backgroundImageStyle(backgroundTint, scale)}
              />
            ) : null}
            <BackgroundTint background={backgroundTint} />

            <DesignSurface perspective={perspective} width={pageW} height={pageH}>
            {layers.map(({ kind, item }) => {
              const isSel = item.id === selectedId;
              const common = {
                'data-item-id': item.id,
                tabIndex: item.hidden ? -1 : 0,
                role: 'button',
                'aria-pressed': isSel,
                onPointerDown: (e) => onItemPointerDown(e, item.id, item),
                onFocus: () => {
                  if (!isSel) onSelect(item.id);
                },
                ...gestureHandlers,
              };
              const cursor = !interactive ? 'pointer' : item.locked ? 'default' : 'move';
              if (kind === 'element') {
                const el = withElementDefaults(item);
                // Unfilled shapes (frames, outline boxes) are only grabbable by
                // their border so items inside them stay clickable.
                const hollow = (el.type === 'rect' || el.type === 'ellipse') && !el.fill;
                const edge = Math.max(10, el.stroke_width * scale + 6);
                return (
                  <ElementView
                    key={item.id}
                    element={item}
                    scale={scale}
                    aria-label={`${itemLabel({ kind, item })}${item.locked ? ' (locked)' : ''}`}
                    className={cn('outline-none', !isSel && interactive && 'hover:outline hover:outline-1 hover:outline-pink/40')}
                    style={{
                      cursor,
                      touchAction: 'none',
                      ...(hollow ? { pointerEvents: 'none' } : {}),
                    }}
                    data-testid={`element-${item.id}`}
                    {...common}
                  >
                    {el.type === 'line' && (
                      // Bigger hit area for thin lines.
                      <span className="absolute left-0 right-0 -top-2 -bottom-2" aria-hidden="true" />
                    )}
                    {hollow &&
                      [
                        { top: -edge / 2, left: -edge / 2, right: -edge / 2, height: edge },
                        { bottom: -edge / 2, left: -edge / 2, right: -edge / 2, height: edge },
                        { top: 0, bottom: 0, left: -edge / 2, width: edge },
                        { top: 0, bottom: 0, right: -edge / 2, width: edge },
                      ].map((pos, i) => (
                        <span key={i} className="absolute" style={{ ...pos, pointerEvents: 'auto' }} aria-hidden="true" />
                      ))}
                  </ElementView>
                );
              }
              const editing = editingId === item.id;
              return (
                <div
                  key={item.id}
                  aria-label={`${item.label || 'Text'}: ${item.text || ''}${item.locked ? ' (locked)' : ''}`}
                  className={cn('outline-none', !isSel && 'hover:outline-dashed hover:outline-1 hover:outline-offset-4 hover:outline-pink/40')}
                  style={{ ...textFieldStyle({ ...item, text: displayText(item, design.text_fields) }, scale), cursor, touchAction: 'none', ...(editing ? { visibility: 'hidden' } : {}) }}
                  data-testid={`text-${item.id}`}
                  title={interactive && !item.locked ? 'Double-click to edit the text here' : undefined}
                  onDoubleClick={(e) => {
                    if (!interactive || item.locked || !onTextEdit) return;
                    e.stopPropagation();
                    onSelect(item.id);
                    setEditingId(item.id);
                  }}
                  {...common}
                >
                  <TextContent field={item} text={displayText(item, design.text_fields)} />
                </div>
              );
            })}

            {editingItem && editBox ? (
              <textarea
                ref={editRef}
                dir="auto"
                value={editingItem.text || ''}
                aria-label={`Edit ${editingItem.label || 'text'}`}
                spellCheck={false}
                onChange={(e) => onTextEdit(editingItem.id, e.target.value)}
                onSelect={(e) => onCaret?.(editingItem.id, e.target.selectionStart, e.target.selectionEnd)}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setEditingId(null);
                  }
                }}
                onBlur={() => setEditingId(null)}
                className="absolute z-30 m-0 p-0 border-0 outline-none resize-none overflow-hidden"
                style={{
                  ...textFieldStyle(editingItem, scale),
                  visibility: 'visible',
                  width: editBox.w + 6,
                  height: editBox.h + 4,
                  maxWidth: 'none',
                  whiteSpace: 'pre-wrap',
                  background: 'rgba(255,255,255,0.25)',
                  boxShadow: '0 0 0 1.5px rgba(236,0,140,0.7)',
                  borderRadius: 3,
                  caretColor: '#EC008C',
                  cursor: 'text',
                }}
                data-testid="inline-text-editor"
              />
            ) : null}

            {showWatermark ? <Watermark scale={scale} /> : null}

            {guides.v.map((gx) => (
              <div key={`v${gx}`} className="absolute top-0 bottom-0 w-px bg-pink/70 pointer-events-none z-10" style={{ left: `${gx}%` }} aria-hidden="true" />
            ))}
            {guides.h.map((gy) => (
              <div key={`h${gy}`} className="absolute left-0 right-0 h-px bg-pink/70 pointer-events-none z-10" style={{ top: `${gy}%` }} aria-hidden="true" />
            ))}

            {multi &&
              Object.entries(rects).map(([id, q]) => (
                <div
                  key={id}
                  className="absolute z-10 pointer-events-none rounded-[3px] border-[1.5px] border-pink"
                  style={{ left: `${q.l}%`, top: `${q.t}%`, width: `${q.r - q.l}%`, height: `${q.b - q.t}%` }}
                  aria-hidden="true"
                  data-testid="multi-outline"
                />
              ))}
            {multi && Object.keys(rects).length > 1 ? (() => {
              const all = Object.values(rects);
              const l = Math.min(...all.map((q) => q.l)) - 0.6;
              const t = Math.min(...all.map((q) => q.t)) - 0.6;
              const r = Math.max(...all.map((q) => q.r)) + 0.6;
              const b = Math.max(...all.map((q) => q.b)) + 0.6;
              return (
                <div
                  className="absolute z-10 pointer-events-none border border-dashed border-blue"
                  style={{ left: `${l}%`, top: `${t}%`, width: `${r - l}%`, height: `${b - t}%` }}
                  aria-hidden="true"
                />
              );
            })() : null}
            {marquee ? (
              <div
                className="absolute z-20 pointer-events-none border border-blue bg-blue/10"
                style={{ left: `${marquee.x0}%`, top: `${marquee.y0}%`, width: `${marquee.x1 - marquee.x0}%`, height: `${marquee.y1 - marquee.y0}%` }}
                aria-hidden="true"
                data-testid="marquee"
              />
            ) : null}

            {selected && !multi && box && !selItem.hidden ? (
              <div
                className="absolute z-10 pointer-events-none"
                style={{
                  left: `${selItem.x ?? 50}%`,
                  top: `${selItem.y ?? 50}%`,
                  width: box.w + overlayPad * 2,
                  height: Math.max(box.h, isLine ? 4 : 0) + overlayPad * 2,
                  transform: `translate(-50%, -50%) rotate(${selItem.rotation || 0}deg)`,
                }}
                aria-hidden="true"
                data-testid="selection-overlay"
              >
                <div
                  className={cn(
                    'absolute inset-0 rounded-[3px] border-[1.5px]',
                    selItem.locked ? 'border-dashed border-[#6B665C]' : 'border-pink'
                  )}
                />
                {showHandles &&
                  corners.map(([sx, sy]) => (
                    <span
                      key={`${sx}${sy}`}
                      className={cn(HANDLE, 'pointer-events-auto', sy > 0 ? 'border-blue' : 'border-pink')}
                      style={{
                        left: `calc(${(sx + 1) * 50}% - 5.5px)`,
                        top: `calc(${(sy + 1) * 50}% - 5.5px)`,
                        cursor: sx * sy > 0 ? 'nwse-resize' : sx * sy < 0 ? 'nesw-resize' : 'ew-resize',
                      }}
                      onPointerDown={(e) => onHandlePointerDown(e, [sx, sy])}
                      data-testid={`handle-${sx}-${sy}`}
                      {...gestureHandlers}
                    />
                  ))}
                {showHandles && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 -bottom-[36px] h-[24px] w-[24px] rounded-full bg-white border border-[#DCD6C8] flex items-center justify-center text-ink pointer-events-auto cursor-grab touch-none shadow-sm"
                    onPointerDown={(e) => onHandlePointerDown(e, 'rotate')}
                    data-testid="handle-rotate"
                    {...gestureHandlers}
                  >
                    <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                )}
              </div>
            ) : null}
            </DesignSurface>
          </div>
        )}
      </div>

      {!compact && footerNote ? (
        <div className="absolute bottom-4 left-5 h-8 px-3 rounded-full bg-white/80 flex items-center text-xs font-semibold text-[#4A4640] pointer-events-none">
          {footerNote}
        </div>
      ) : null}

      <div
        role="group"
        aria-label="Zoom"
        className={cn(
          'absolute flex items-center gap-0.5 p-1 rounded-full gradient-border z-10',
          compact ? 'bottom-2 right-2 scale-90 origin-bottom-right' : 'bottom-4 right-5'
        )}
      >
        <button type="button" aria-label="Zoom out" onClick={() => zoomBy(1 / 1.25)} className={cn('h-8 w-8 rounded-full flex items-center justify-center hover:bg-cream', FOCUS_RING)}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="w-12 text-center text-[13px] font-extrabold tabular-nums" aria-live="polite" data-testid="zoom-level">
          {Math.round(scale * 100)}%
        </span>
        <button type="button" aria-label="Zoom in" onClick={() => zoomBy(1.25)} className={cn('h-8 w-8 rounded-full flex items-center justify-center hover:bg-cream', FOCUS_RING)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" aria-label="Fit to screen" title="Fit to screen" onClick={() => setZoom(1)} className={cn('h-8 w-8 rounded-full flex items-center justify-center hover:bg-cream', FOCUS_RING)}>
          <Maximize className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default StudioCanvas;
